import { FC } from 'react';
import { resolveAgentRoute } from './agentRouteMap';

/**
 * Render de los mensajes del chat de Paulina — extraído de AgentChatWidget para
 * que el panel desktop (PaulinaPanel) y el bottom-sheet móvil (PaulinaSheet)
 * reusen EXACTAMENTE el mismo molde sin duplicar la lógica de parseo. Mantiene el
 * contrato de navegación por llave (ACCION_IR → resolveAgentRoute), el botón de
 * soporte (ACCION_SOPORTE), imágenes, links markdown/sueltos y **negritas**.
 */

const IMAGE_URL = /^(?:captura:\s*)?(https?:\/\/\S+\.(?:jpe?g|png|webp))\s*$/i;
const NAV_LINE = /^ACCION_IR:(\w+)$/;
const SOPORTE_LINE = /^ACCION_SOPORTE:(https?:\/\/\S+)$/;
const MD_IMAGE = /!\[[^\]]*\]\(\s*(https?:\/\/[^)\s]+)\s*\)/g;
const MD_LINK = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
const LINK = /(https?:\/\/[^\s)]+)/g;

const linkClass = 'break-all text-[#4298B5] underline';

const renderLinks = (text: string, key: string) =>
  // Test stateless: LINK es global (lastIndex stateful); .test() en bucle falla
  // intermitentemente. Los fragmentos capturados por split que son URL empiezan con http(s).
  text.split(LINK).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={`${key}-${i}`} href={part} target="_blank" rel="noreferrer" className={linkClass}>
        {part}
      </a>
    ) : (
      part
    ),
  );

// Texto inline: links markdown [texto](url), URLs sueltas y **negritas**.
const renderInline = (text: string, key: string) => {
  const parts = text.split(MD_LINK); // [texto, label, url, texto, ...]
  const out = [];
  for (let i = 0; i < parts.length; i += 3) {
    const plain = parts[i] ?? '';
    out.push(
      ...plain
        .split(/\*\*([^*]+)\*\*/g)
        .map((p, j) =>
          j % 2 === 1 ? <strong key={`${key}-b${i}-${j}`}>{p}</strong> : renderLinks(p, `${key}-${i}-${j}`),
        ),
    );
    if (i + 2 < parts.length) {
      out.push(
        <a key={`${key}-l${i}`} href={parts[i + 2]} target="_blank" rel="noreferrer" className={linkClass}>
          {parts[i + 1]}
        </a>,
      );
    }
  }
  return out;
};

// Render ligero del mensaje: imágenes, links, negritas y botón de navegación.
export const MessageContent: FC<{ text: string; onNavigate: (seccion: string) => void }> = ({ text, onNavigate }) => {
  // Las imágenes markdown ![alt](url) se separan a su propia línea como URL.
  const normalized = text.replace(MD_IMAGE, '\n$1\n');
  return (
    <>
      {normalized.split('\n').map((line, i) => {
        const sop = line.trim().match(SOPORTE_LINE);
        if (sop) {
          return (
            <a
              key={i}
              href={sop[1]}
              target="_blank"
              rel="noreferrer"
              className="my-1 flex w-full items-center justify-center gap-2 rounded-lg bg-[#25D366] px-3 py-2 text-center text-sm font-semibold text-[#ffffff]"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.5A10 10 0 1 0 12 2zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-2.9.9.9-2.8-.2-.3A8 8 0 1 1 12 20zm4.4-5.6c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1-.6.8-.8 1-.3.2-.5.1a6.6 6.6 0 0 1-1.9-1.2 7.3 7.3 0 0 1-1.4-1.7c-.1-.2 0-.4.1-.5l.4-.4.2-.4a.5.5 0 0 0 0-.5l-.8-1.9c-.2-.5-.4-.4-.5-.4h-.5a.9.9 0 0 0-.7.3 2.8 2.8 0 0 0-.9 2.1 4.9 4.9 0 0 0 1 2.6 11 11 0 0 0 4.3 3.8c.6.3 1.1.4 1.5.5a3.5 3.5 0 0 0 1.6.1c.5-.1 1.4-.6 1.6-1.1s.2-1 .1-1.1z" />
              </svg>
              Hablar con soporte →
            </a>
          );
        }
        const nav = line.trim().match(NAV_LINE);
        if (nav) {
          const seccion = nav[1];
          if (!resolveAgentRoute(seccion)) return null; // llave desconocida → sin botón roto
          return (
            <button
              key={i}
              type="button"
              onClick={() => onNavigate(seccion)}
              className="my-1 w-full rounded-lg bg-[#4298B5] px-3 py-2 text-left text-sm font-semibold text-[#ffffff]"
            >
              Llévame a {seccion.replace(/_/g, ' ')} →
            </button>
          );
        }
        const img = line.trim().match(IMAGE_URL);
        if (img) {
          return (
            <img
              key={i}
              src={img[1]}
              alt="Captura del panel"
              loading="lazy"
              className="my-1 w-full rounded-lg border border-[#e2e8f0]"
            />
          );
        }
        return (
          <p key={i} className="my-0.5 min-h-[0.5rem] whitespace-pre-wrap break-words">
            {renderInline(line, `${i}`)}
          </p>
        );
      })}
    </>
  );
};
